/**
 * Punto de recogida de un tour de catálogo.
 *
 * El huésped elige dónde lo recogen: por defecto el punto de encuentro oficial
 * del tour, o cualquier dirección/punto de referencia de El Salvador (un mall,
 * un hotel, el aeropuerto). El modal es el mismo `LocationSearchModal` que ya
 * usan la cotización de tours y los servicios, así que la búsqueda, el debounce
 * y el contrato E2E no se duplican.
 */
import { useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { MapPin } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { LocationSearchModal, type SearchLocation } from '@/components/location/LocationSearchModal'
import { useTheme } from '@/lib/hooks/use-theme'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import type { TourTravelInfo } from '@/lib/types/tours.types'

/**
 * Puntos de referencia que un salvadoreño dice de memoria para que lo recojan.
 *
 * Mapbox no tiene indexados los landmarks de El Salvador (buscaba "Multiplaza"
 * y devolvía plazas por similitud), así que estos chips no son consultas: son
 * lugares con coordenadas VERIFICADAS — cada punto salió de OpenStreetMap y su
 * dirección se comprobó con Mapbox reverse, país SV incluido. Un chip elige el
 * lugar exacto; la búsqueda libre queda para todo lo demás.
 */
export const TOUR_START_POINT_REFERENCE_POINTS: readonly SearchLocation[] = [
  {
    label: 'Multiplaza',
    name: 'Multiplaza',
    address: 'Calle El Pedregal 02, 01502 Antiguo Cuscatlán',
    latitude: 13.6795108,
    longitude: -89.2483987,
  },
  {
    label: 'Plaza Salvador del Mundo',
    name: 'Plaza Salvador del Mundo',
    address: 'Redondel El Salvador del Mundo, 01101 San Salvador',
    latitude: 13.7012746,
    longitude: -89.2244537,
  },
  {
    label: 'Aeropuerto Internacional de El Salvador',
    name: 'Aeropuerto Internacional de El Salvador',
    address: 'Aeropuerto de El Salvador, 01616 San Luis Talpa',
    latitude: 13.4464583,
    longitude: -89.053554,
  },
  {
    label: 'Centro Histórico de San Salvador',
    name: 'Centro Histórico de San Salvador',
    address: 'Plaza Gerardo Barrios, 01101 San Salvador',
    latitude: 13.697617,
    longitude: -89.1911714,
  },
  {
    label: 'Metrocentro',
    name: 'Metrocentro',
    address: 'Boulevard de los Héroes, 01101 San Salvador',
    latitude: 13.7068193,
    longitude: -89.2129545,
  },
  {
    label: 'La Gran Vía',
    name: 'La Gran Vía',
    address: 'Carretera Panamericana, 01502 Antiguo Cuscatlán',
    latitude: 13.6769149,
    longitude: -89.2542953,
  },
]

interface TourStartPointFieldProps {
  meetingPoint: TourTravelInfo['meetingPoint'] | null | undefined
  /** Fallback cuando el tour no publica punto de encuentro. */
  originLabel: string
  value: SearchLocation | null
  onChange: (value: SearchLocation | null) => void
}

export function TourStartPointField({
  meetingPoint,
  originLabel,
  value,
  onChange,
}: TourStartPointFieldProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [pickerVisible, setPickerVisible] = useState(false)

  const defaultLabel = meetingPoint?.label?.trim() || originLabel
  const defaultAddress = meetingPoint?.address?.trim() || null
  const selectedLabel = value ? value.name?.trim() || value.label : null
  const selectedAddress = value ? value.address?.trim() || value.label : null

  return (
    <View testID="tours.start-point-field">
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
        {t('tours.start_point.title')}
      </Text>
      <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
        {t('tours.start_point.hint')}
      </Text>
      <Pressable
        onPress={() => setPickerVisible(true)}
        accessibilityRole="button"
        accessibilityLabel={t('tours.start_point.change')}
        testID="tours.start-point-change"
        style={({ pressed }) => ({ marginTop: SPACING.sm, opacity: pressed ? 0.85 : 1 })}
      >
        <View
          style={{
            borderWidth: 1,
            borderColor: value ? colors.primary : colors.border,
            borderRadius: RADIUS.md,
            backgroundColor: colors.surface,
            padding: SPACING.md,
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.sm,
            minHeight: INTERACTION.minTouchTarget,
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: RADIUS.full,
              backgroundColor: colors.surfaceSubtle,
            }}
          >
            <MapPin size={20} color={colors.primary} strokeWidth={2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              numberOfLines={2}
              style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}
              testID="tours.start-point-value"
            >
              {value ? selectedLabel : defaultLabel}
            </Text>
            {value ? (
              <>
                {selectedAddress ? (
                  <Text
                    numberOfLines={2}
                    style={{
                      marginTop: 2,
                      fontFamily: FONTS.body,
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {selectedAddress}
                  </Text>
                ) : null}
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    color: colors.textMuted,
                  }}
                >
                  {t('tours.start_point.pickup_hint')}
                </Text>
              </>
            ) : (
              <>
                {defaultAddress ? (
                  <Text
                    numberOfLines={2}
                    style={{
                      marginTop: 2,
                      fontFamily: FONTS.body,
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {defaultAddress}
                  </Text>
                ) : null}
                <View
                  style={{
                    marginTop: 3,
                    alignSelf: 'flex-start',
                    paddingHorizontal: SPACING.sm,
                    paddingVertical: 2,
                    borderRadius: RADIUS.full,
                    backgroundColor: colors.surfaceSubtle,
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('tours.start_point.default_badge')}
                  </Text>
                </View>
              </>
            )}
          </View>
          <Text
            style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}
            testID="tours.start-point-action"
          >
            {value ? t('tours.start_point.change') : t('tours.start_point.choose')}
          </Text>
        </View>
      </Pressable>
      {value ? (
        <Pressable
          onPress={() => onChange(null)}
          accessibilityRole="button"
          accessibilityLabel={t('tours.start_point.use_default')}
          testID="tours.start-point-reset"
          style={({ pressed }) => ({ marginTop: SPACING.xs, opacity: pressed ? 0.7 : 1 })}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textAccent,
            }}
          >
            {t('tours.start_point.use_default')}
          </Text>
        </Pressable>
      ) : null}
      <LocationSearchModal
        visible={pickerVisible}
        title={t('tours.start_point.search_title')}
        onClose={() => setPickerVisible(false)}
        onPick={(location) => {
          onChange(location)
          setPickerVisible(false)
        }}
        testIDPrefix="tours.start-point"
        placeholder={t('tours.start_point.search_placeholder')}
        searchingLabel={t('tours.start_point.searching')}
        emptyLabel={t('tours.start_point.search_empty')}
        errorLabel={t('tours.start_point.search_error')}
        quickPicks={TOUR_START_POINT_REFERENCE_POINTS}
        quickPicksLabel={t('tours.start_point.reference_points')}
      />
    </View>
  )
}
